/**
 * ChatWidget - floating support chat for clients (bottom-right FAB).
 */
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { MessageCircle, X, Send, Loader2, CheckCheck, Headphones } from "lucide-react";
import { api, getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useChatSocket } from "@/lib/useChatSocket";
import { fmtTime, fmtDateTime } from "@/lib/date";

export default function ChatWidget() {
    const { user, token } = useAuth();
    const [open, setOpen] = useState(false);
    const [conv, setConv] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [presence, setPresence] = useState({ online_count: 0, online_names: [] });
    const [unread, setUnread] = useState(0);
    const scrollRef = useRef(null);

    const onEvent = (evt) => {
        if (evt.type === "new_message") {
            if (conv && evt.conversation?.id === conv.id) {
                setMessages((m) => [...m, evt.message]);
                setConv((c) => evt.conversation || c);
                if (open && evt.message.sender_role !== "client") {
                    api.post(`/chat/conversations/${conv.id}/read`).catch(() => {});
                }
            } else if (evt.message.sender_role !== "client" && evt.conversation?.client_id === user?.id) {
                setUnread((u) => u + 1);
            }
        } else if (evt.type === "presence") {
            setPresence({
                online_count: evt.online_count || 0,
                online_names: evt.online_names || [],
            });
        } else if (evt.type === "conversation_updated") {
            if (conv && evt.conversation?.id === conv.id) {
                setConv(evt.conversation);
            }
        }
    };

    const { status, send } = useChatSocket({ enabled: !!user, token, onEvent });

    // Initial: presence count
    useEffect(() => {
        if (!user) return;
        api.get("/chat/agents/online").then((r) => setPresence(r.data)).catch(() => {});
    }, [user]);

    // Pull initial conversation when widget opens
    useEffect(() => {
        if (!open || !user) return;
        setUnread(0);
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.post("/chat/conversations", {});
                setConv(data);
                const msgs = await api.get(`/chat/conversations/${data.id}/messages`);
                setMessages(msgs.data);
                api.post(`/chat/conversations/${data.id}/read`).catch(() => {});
            } catch (e) {
                toast.error(getApiErrorMessage(e));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Auto scroll
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open]);

    const submit = async (e) => {
        e?.preventDefault?.();
        const body = input.trim();
        if (!body || !conv) return;
        setSending(true);
        // optimistic
        const optimistic = {
            id: `tmp-${Date.now()}`,
            sender_role: "client",
            sender_id: user.id,
            sender_name: user.name,
            body,
            created_at: new Date().toISOString(),
            _optimistic: true,
        };
        setMessages((m) => [...m, optimistic]);
        setInput("");
        try {
            const sent = send({ type: "send_message", conversation_id: conv.id, body });
            if (!sent) {
                // fallback REST
                await api.post(`/chat/conversations/${conv.id}/messages`, { body });
            }
        } catch (err) {
            toast.error(getApiErrorMessage(err));
            setMessages((m) => m.filter((x) => x.id !== optimistic.id));
        } finally {
            setSending(false);
        }
    };

    if (!user || user.role !== "client") return null;

    return (
        <>
            {/* FAB */}
            <button
                onClick={() => setOpen((v) => !v)}
                className="fixed z-[10000] bottom-24 right-4 lg:bottom-6 lg:right-[210px] h-14 w-14 rounded-full btn-grad-purple text-white shadow-2xl flex items-center justify-center hover:scale-105 transition-transform"
                data-testid="chat-fab-button"
                aria-label="Abrir chat de suporte"
            >
                {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
                {!open && unread > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#FB7185] text-white text-xs font-bold grid place-items-center">
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
                {!open && presence.online_count > 0 && (
                    <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-[#2DD4BF] border-2 border-[#0B0B10]" title="Suporte online" />
                )}
            </button>

            {/* Panel */}
            {open && (
                <Card
                    className="fixed z-[10000] bottom-40 right-4 lg:bottom-24 lg:right-[210px] w-[min(92vw,380px)] h-[min(75vh,560px)] flex flex-col rounded-2xl glass-card border-white/15 overflow-hidden"
                    data-testid="chat-panel"
                >
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
                        <span className="h-9 w-9 rounded-xl btn-grad-purple grid place-items-center text-white">
                            <Headphones className="h-4 w-4" />
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-semibold leading-tight">Suporte MeSafa</div>
                            <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                                {presence.online_count > 0 ? (
                                    <>
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#2DD4BF]" />
                                        {presence.online_count} técnico(s) online
                                    </>
                                ) : (
                                    <>
                                        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                                        Equipa offline · responderemos por email
                                    </>
                                )}
                            </div>
                        </div>
                        <span className={`text-[10px] uppercase font-semibold ${status === "connected" ? "text-[#2DD4BF]" : "text-muted-foreground"}`}>
                            {status === "connected" ? "● live" : status === "connecting" ? "●..." : "offline"}
                        </span>
                    </div>

                    {/* Messages */}
                    <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin px-4 py-3 space-y-2">
                        {loading && (
                            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                        )}
                        {!loading && messages.length === 0 && (
                            <div className="text-center text-sm text-muted-foreground py-8">
                                Diga olá! 👋<br />
                                A nossa equipa responde em poucos minutos.
                            </div>
                        )}
                        {messages.map((m) => (
                            <MessageBubble key={m.id} msg={m} isMine={m.sender_role === "client"} />
                        ))}
                        {conv?.status === "resolvida" && (
                            <div className="text-center text-xs text-muted-foreground py-2 border-t border-white/5 mt-2 pt-2">
                                Conversa marcada como resolvida.
                            </div>
                        )}
                    </div>

                    {/* Input */}
                    <form onSubmit={submit} className="border-t border-white/10 p-3 flex gap-2">
                        <Input
                            placeholder="Escreva a sua mensagem..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            disabled={sending || !conv || conv.status === "resolvida"}
                            data-testid="chat-input"
                        />
                        <Button
                            type="submit"
                            size="icon"
                            className="btn-grad-purple text-white"
                            disabled={sending || !input.trim() || !conv || conv.status === "resolvida"}
                            data-testid="chat-send-button"
                        >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                    </form>
                </Card>
            )}
        </>
    );
}

function MessageBubble({ msg, isMine }) {
    return (
        <div className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
            <div
                className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${
                    isMine
                        ? "btn-grad-purple text-white rounded-br-md"
                        : "bg-white/8 border border-white/10 rounded-bl-md"
                }`}
            >
                {!isMine && (
                    <div className="text-[11px] font-semibold text-[#A78BFA] mb-0.5">{msg.sender_name}</div>
                )}
                <div className="whitespace-pre-line break-words">{msg.body}</div>
                <div className={`text-[10px] mt-1 ${isMine ? "text-white/70" : "text-muted-foreground"} flex items-center gap-1`}>
                    {fmtTime(msg.created_at)}
                    {isMine && msg.read_by_agent && <CheckCheck className="h-3 w-3" />}
                </div>
            </div>
        </div>
    );
}
